import type { BufferGeometry } from 'three';
import type { Vec3 } from '../../geo/types';
import type { PaletteModel } from '../../data/types';
import type { Rng } from '../../core/rng';
import { sampleSwatch } from '../../data/palette';
import { hexToLinear } from '../../core/color';
import { createMeshBuilder, type Frame, type VertexStyle } from '../mesh/meshBuilder';
import { SURFACE, EDGE_ID } from '../mesh/attributes';
import { boxSolid, hexahedronSolid } from './solids';
import { OPENING } from './openings';
import { DETAIL_KINDS, type DetailKind } from './detailSink';
import { ANCHOR } from './wallAnchors';
import { WARE_BASKET } from './wareBasket';

/**
 * LOD1 details in front of the flush LOD0 openings: closed boxes of joinery in metres for an
 * opening of the reference size, x across it from its left edge, y up from its bottom, z out of
 * the wall. Instances stretch them to each opening's size.
 */
/** What a part is painted as: a frame's light, a leaf's wood, a shutter's red-brown, a step's stone, an anchor's iron, or a basket and its loaves. */
export type DetailMaterial = 'frame' | 'leaf' | 'shutter' | 'stone' | 'iron' | 'wicker' | 'bread';

/** A hinged part turned about its hinge, parallel to x through (y, z) = `pivot`: its lower end swings out by `angle`. */
export interface Turn {
  readonly pivot: readonly [number, number];
  readonly angle: number;
}

export interface DetailPart {
  readonly name: string;
  readonly min: Vec3;
  readonly max: Vec3;
  readonly material: DetailMaterial;
  /** The box between `min` and `max` is the part as it hangs; this turns it to where it stands. */
  readonly turn?: Turn;
}

export interface DetailParts {
  readonly width: number;
  readonly height: number;
  readonly parts: readonly DetailPart[];
}

/** How far joinery stands proud of the wall. */
export const DETAIL_PROTRUSION = { min: 0.03, max: 0.08 } as const;

/**
 * Measured on the painting's window row (docs/reference/crops/window-row.png): the open shutters
 * beside the windows are red-brown, median #3f1900 (L* 14.0), and the frames are its lightest
 * strokes, in highlightWhite.
 */
export const JOINERY = { shutter: '#3f1900' } as const;

/**
 * A shop's shutters (Rouwhorst, Binnenstad 214 and 222): the lower one lowered level onto the sill as
 * the counter, on two chains from the jambs, the upper one raised as an awning. Plank thickness,
 * chains and the awning's angle are estimates (docs/plans/houses.md, "Shops"); at 70 degrees the
 * awning of an opening of the reference height keeps its free edge 8 voet (2.26 m) over the street,
 * as the keur of about 1413 asked of luifels (Breen).
 */
export const SHOP_SHUTTERS = {
  plank: 0.04,
  awning: (70 * Math.PI) / 180,
  chain: 0.02,
  chainInset: 0.05,
  chainEnd: 0.03,
} as const;

const FRAME = { bar: 0.08, depth: 0.06 } as const;
const SILL = { height: 0.07, ears: 0.04, depth: 0.08 } as const;
const INFILL_DEPTH = 0.035;
const BAR_DEPTH = 0.05;
const STEP = { height: 0.08, nose: 0.1, depth: 0.08 } as const;
const SMALL = { bar: 0.05, depth: 0.04, sill: 0.05, ears: 0.03, sillDepth: 0.06 } as const;
const REFERENCE = {
  window: [1.3, 1.9],
  doorLeaf: [OPENING.door.width, OPENING.door.leaf],
  doorTransom: [OPENING.door.width, OPENING.door.transom],
  loftDoor: [OPENING.loftDoor.width, OPENING.loftDoor.height],
  smallWindow: [OPENING.smallWindow.width, OPENING.smallWindow.gableHeight],
  shop: [2.2, 1.8],
} as const;

function part(
  name: string,
  min: Vec3,
  max: Vec3,
  material: DetailMaterial = 'frame',
  turn?: Turn
): DetailPart {
  return turn ? { name, min, max, material, turn } : { name, min, max, material };
}

function turned(p: Vec3, turn: Turn | undefined): Vec3 {
  if (!turn) return p;
  const [py, pz] = turn.pivot;
  const [dy, dz] = [p[1] - py, p[2] - pz];
  const [c, s] = [Math.cos(turn.angle), Math.sin(turn.angle)];
  return [p[0], py + dy * c + dz * s, pz - dy * s + dz * c];
}

/** A part's eight corners where it stands; bits 0, 1 and 2 of a corner's index take x, y and z from `max`. */
export function partCorners(part: DetailPart): Vec3[] {
  return Array.from({ length: 8 }, (_, i) => {
    const pick = (axis: 0 | 1 | 2): number => ((i >> axis) & 1 ? part.max : part.min)[axis];
    return turned([pick(0), pick(1), pick(2)], part.turn);
  });
}

/** Two jambs from `y0` up, and a head between them. */
function frame(
  width: number,
  height: number,
  y0: number,
  bar: number,
  depth: number
): DetailPart[] {
  return [
    part('jamb', [0, y0, 0], [bar, height, depth]),
    part('jamb', [width - bar, y0, 0], [width, height, depth]),
    part('head', [bar, height - bar, 0], [width - bar, height, depth]),
  ];
}

/** The kruiskozijn's lines at the reference size: the mullion's middle and half width, the transom's foot and head. */
const CROSS = (() => {
  const [width, height] = REFERENCE.window;
  const t0 = OPENING.transomAt * height - FRAME.bar / 2;
  return { width, height, mid: width / 2, half: OPENING.mullion / 2, t0, t1: t0 + FRAME.bar };
})();

/** A glazed light in its detail's unit space: x over the opening's width, y over the detail's height. */
export interface UnitLight {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/*
 * The lights glazed in leaded glass (STYLE §15.1). Of a cross window only the two upper lights were
 * glazed, the lower ones shut with shutters (Monumentenstad 2001, p. 12, after Wikipedia NL
 * "Kruiskozijn"); the left upper light is given, the right one lies past the mullion. A door's
 * transom light is the whole opening of its frame.
 */
export const LEADED_LIGHT: Readonly<Record<'window' | 'doorTransom', UnitLight>> = {
  window: {
    x: FRAME.bar / CROSS.width,
    y: CROSS.t1 / CROSS.height,
    width: (CROSS.mid - CROSS.half - FRAME.bar) / CROSS.width,
    height: (CROSS.height - FRAME.bar - CROSS.t1) / CROSS.height,
  },
  doorTransom: {
    x: FRAME.bar / REFERENCE.doorTransom[0],
    y: 0,
    width: 1 - (2 * FRAME.bar) / REFERENCE.doorTransom[0],
    height: 1 - FRAME.bar / REFERENCE.doorTransom[1],
  },
};

function kruiskozijn(): DetailParts {
  const { width, height, mid, half, t0, t1 } = CROSS;
  const { bar } = FRAME;
  const bottom = SILL.height;
  const lights = [
    [bar, mid - half],
    [mid + half, width - bar],
  ] as const;
  const light = mid - half - bar;
  return {
    width,
    height,
    parts: [
      part('sill', [-SILL.ears, 0, 0], [width + SILL.ears, SILL.height, SILL.depth]),
      ...frame(width, height, bottom, bar, FRAME.depth),
      part('mullion', [mid - half, bottom, 0], [mid + half, height - bar, BAR_DEPTH]),
      ...lights.map(([x0, x1]) => part('transom', [x0, t0, 0], [x1, t1, BAR_DEPTH])),
      part('shutter', [-light, bottom, 0], [0, t0, INFILL_DEPTH], 'shutter'),
      part('shutter', [width, bottom, 0], [width + light, t0, INFILL_DEPTH], 'shutter'),
      ...[-light, width].flatMap((x) =>
        [bottom + 0.12, t0 - 0.15].map((y) =>
          part(
            'shutter-strap',
            [x + 0.02, y, INFILL_DEPTH + 0.003],
            [x + light - 0.02, y + 0.025, INFILL_DEPTH + 0.009],
            'iron'
          )
        )
      ),
    ],
  };
}

function doorLeaf(): DetailParts {
  const [width, height] = REFERENCE.doorLeaf;
  const { bar } = FRAME;
  return {
    width,
    height,
    parts: [
      part('step', [-STEP.nose, 0, 0], [width + STEP.nose, STEP.height, STEP.depth], 'stone'),
      part('jamb', [0, STEP.height, 0], [bar, height, FRAME.depth]),
      part('jamb', [width - bar, STEP.height, 0], [width, height, FRAME.depth]),
      // Narrow shadow gaps between separate vertical boards, with a plain forged latch.
      ...Array.from({ length: 5 }, (_, i) => {
        const plank = (width - bar * 2) / 5;
        return part(
          'leaf',
          [bar + i * plank + 0.002, STEP.height, 0],
          [bar + (i + 1) * plank - 0.002, height - bar, INFILL_DEPTH],
          'leaf'
        );
      }),
      ...[0.28, height - 0.35].map((y) =>
        part(
          'hinge-strap',
          [bar + 0.01, y, INFILL_DEPTH + 0.003],
          [bar + 0.28, y + 0.03, INFILL_DEPTH + 0.012],
          'iron'
        )
      ),
      part(
        'latch',
        [width - bar - 0.16, height * 0.52, INFILL_DEPTH + 0.005],
        [width - bar - 0.04, height * 0.52 + 0.027, INFILL_DEPTH + 0.015],
        'iron'
      ),
      part(
        'handle',
        [width - bar - 0.08, height * 0.52 - 0.055, INFILL_DEPTH + 0.012],
        [width - bar - 0.055, height * 0.52 + 0.045, 0.065],
        'iron'
      ),
      part('rail', [bar, height - bar, 0], [width - bar, height, BAR_DEPTH]),
    ],
  };
}

function doorTransom(): DetailParts {
  const [width, height] = REFERENCE.doorTransom;
  return { width, height, parts: frame(width, height, 0, FRAME.bar, FRAME.depth) };
}

/* No hoist beam: Amsterdam houses gained them about 1600, and goods went up inside (docs/plans/houses.md). */
function loftDoor(): DetailParts {
  const [width, height] = REFERENCE.loftDoor;
  const { bar } = FRAME;
  const mid = width / 2;
  const gap = 0.01;
  return {
    width,
    height,
    parts: [
      ...frame(width, height, 0, bar, FRAME.depth),
      part('bottom', [bar, 0, 0], [width - bar, bar, FRAME.depth]),
      part('leaf', [bar, bar, 0], [mid - gap, height - bar, INFILL_DEPTH], 'leaf'),
      part('leaf', [mid + gap, bar, 0], [width - bar, height - bar, INFILL_DEPTH], 'leaf'),
      ...[bar + 0.01, width - bar - 0.23].flatMap((x) =>
        [0.25, height - 0.28].map((y) =>
          part(
            'loft-hinge',
            [x, y, INFILL_DEPTH + 0.003],
            [x + 0.22, y + 0.025, INFILL_DEPTH + 0.012],
            'iron'
          )
        )
      ),
      part(
        'loft-latch',
        [mid - 0.08, height * 0.48, INFILL_DEPTH + 0.004],
        [mid + 0.08, height * 0.48 + 0.025, INFILL_DEPTH + 0.018],
        'iron'
      ),
    ],
  };
}

/** A chain from a jamb's face down to the counter's outer edge, 45 degrees out, its lowest corner on the counter. */
function counterChain(x0: number, counterTop: number, reach: number): DetailPart {
  const { chain, chainEnd } = SHOP_SHUTTERS;
  const turn = Math.PI / 4;
  const pz = FRAME.depth;
  const length = (reach - chainEnd - pz) / Math.sin(turn);
  const py = counterTop + length * Math.cos(turn) + (chain / 2) * Math.sin(turn);
  const min: Vec3 = [x0, py - length, pz - chain / 2];
  return part('chain', min, [x0 + chain, py, pz + chain / 2], 'leaf', {
    pivot: [py, pz],
    angle: turn,
  });
}

function shop(): DetailParts {
  const [width, height] = REFERENCE.shop;
  const { bar } = FRAME;
  const { plank, awning, chain, chainInset } = SHOP_SHUTTERS;
  const head = height - bar;
  const hung = (head - SILL.height) / 2;
  const counterTop = SILL.height + plank;
  return {
    width,
    height,
    parts: [
      part('sill', [-SILL.ears, 0, 0], [width + SILL.ears, SILL.height, SILL.depth]),
      ...frame(width, height, SILL.height, bar, FRAME.depth),
      part('counter', [bar, SILL.height, 0], [width - bar, counterTop, hung], 'shutter'),
      counterChain(bar + chainInset, counterTop, hung),
      counterChain(width - bar - chainInset - chain, counterTop, hung),
      part(
        'awning',
        [bar, head - hung, FRAME.depth],
        [width - bar, head, FRAME.depth + plank],
        'shutter',
        {
          pivot: [head, FRAME.depth],
          angle: awning,
        }
      ),
    ],
  };
}

/** Where a shop's shutters stand in an opening of this size, in metres from its bottom and out of the wall. */
export interface ShopShutterLines {
  /** Half the jamb's width from the opening's sides. */
  readonly jamb: number;
  readonly counterBottom: number;
  readonly counterTop: number;
  /** How far the counter reaches out: the template does not stretch out of the wall. */
  readonly reach: number;
  readonly head: number;
  /** The awning's underside a share `f` of the way from its hinge to its free edge, as [up, out]. */
  readonly awningAt: (f: number) => readonly [number, number];
}

export function shopShutterLines(width: number, height: number): ShopShutterLines {
  const [sx, sy] = [width / REFERENCE.shop[0], height / REFERENCE.shop[1]];
  const head = REFERENCE.shop[1] - FRAME.bar;
  const hung = (head - SILL.height) / 2;
  const { plank, awning } = SHOP_SHUTTERS;
  return {
    jamb: (FRAME.bar / 2) * sx,
    counterBottom: SILL.height * sy,
    counterTop: (SILL.height + plank) * sy,
    reach: hung,
    head: head * sy,
    awningAt: (f) => [
      (head - f * hung * Math.cos(awning)) * sy,
      FRAME.depth + f * hung * Math.sin(awning),
    ],
  };
}

function smallWindow(): DetailParts {
  const [width, height] = REFERENCE.smallWindow;
  return {
    width,
    height,
    parts: [
      part('sill', [-SMALL.ears, 0, 0], [width + SMALL.ears, SMALL.sill, SMALL.sillDepth]),
      ...frame(width, height, SMALL.sill, SMALL.bar, SMALL.depth),
    ],
  };
}

/*
 * A wall anchor (Rouwhorst, Binnenstad 240): the bar (schieter) upright through the eye of its tie
 * (veer), and the nose forged at its foot so it cannot slip down. Bar and eye sections are estimates.
 */
function anchor(): DetailParts {
  const { width, length } = ANCHOR;
  const bar: [number, number] = [width / 2 - 0.015, width / 2 + 0.015];
  const mid = length / 2;
  return {
    width,
    height: length,
    parts: [
      part('bar', [bar[0], 0, 0], [bar[1], length, 0.03], 'iron'),
      part('eye', [0, mid - 0.03, 0], [width, mid + 0.03, 0.04], 'iron'),
      part('nose', [bar[0] + 0.005, 0.01, 0], [bar[1] - 0.005, 0.06, 0.05], 'iron'),
    ],
  };
}

/*
 * A basket of round loaves on a baker's counter, as the Master of Alkmaar paints it (1504, panel 1): a
 * low wicker basket, its rim, and the loaves heaped above it, at its own size.
 */
function loaves(): DetailParts {
  const { width, height, depth, body, rim, loaf, heap } = WARE_BASKET;
  return {
    width,
    height,
    parts: [
      part('basket', [0, 0, 0], [width, body, depth], 'wicker'),
      part('rim', [-rim, body - rim, -rim], [width + rim, body + rim, depth + rim], 'wicker'),
      ...heap.map(([x, z]) =>
        part(
          'loaf',
          [x - loaf.half, body - loaf.sink, z - loaf.half],
          [x + loaf.half, height, z + loaf.half],
          'bread'
        )
      ),
    ],
  };
}

export const DETAIL_PARTS: Readonly<Record<DetailKind, DetailParts>> = {
  window: kruiskozijn(),
  doorLeaf: doorLeaf(),
  doorTransom: doorTransom(),
  loftDoor: loftDoor(),
  smallWindow: smallWindow(),
  shop: shop(),
  anchor: anchor(),
  loaves: loaves(),
};

const IDENTITY: Frame = { origin: [0, 0, 0], yaw: 0 };
const CLOSED = { top: true, bottom: true } as const;

/** One template per kind, scaled to a unit opening so an instance matrix sets its size. */
function template(
  parts: DetailParts,
  styles: Readonly<Record<DetailMaterial, VertexStyle>>
): BufferGeometry {
  const builder = createMeshBuilder();
  const unit = (p: Vec3): Vec3 => [p[0] / parts.width, p[1] / parts.height, p[2]];
  for (const p of parts.parts) {
    if (p.turn) hexahedronSolid(builder, styles[p.material], IDENTITY, partCorners(p).map(unit));
    else boxSolid(builder, styles[p.material], IDENTITY, unit(p.min), unit(p.max), CLOSED);
  }
  return builder.build();
}

/**
 * Frames light as the painting's window row, leaves in shipHull, shutters its red-brown, steps in
 * towerStone (STYLE §8.1), and anchors black, in the painting's darkest linework. The basket takes the
 * light ochre of the painting's pale walls and the loaves its tile orange-brown, the classes nearest
 * the Alkmaar panel's straw and crust.
 */
export function buildDetailTemplates(
  palette: PaletteModel,
  rng: Rng
): ReadonlyMap<DetailKind, BufferGeometry> {
  const base = {
    edgeId: EDGE_ID.firstObject,
    seed: rng.next(),
    strokeAngle: 0,
    extent: [0, 0] as const,
  };
  const styles: Record<DetailMaterial, VertexStyle> = {
    frame: {
      ...base,
      color: sampleSwatch(palette.highlightWhite, rng.fork('frame')),
      surface: SURFACE.stone,
    },
    leaf: {
      ...base,
      color: sampleSwatch(palette.shipHull, rng.fork('wood')),
      surface: SURFACE.wood,
    },
    shutter: { ...base, color: hexToLinear(JOINERY.shutter), surface: SURFACE.wood },
    stone: {
      ...base,
      color: sampleSwatch(palette.towerStone, rng.fork('stone')),
      surface: SURFACE.stone,
    },
    iron: {
      ...base,
      color: sampleSwatch(palette.lineworkDark, rng.fork('iron')),
      surface: SURFACE.stone,
    },
    wicker: {
      ...base,
      color: sampleSwatch(palette.wallLight, rng.fork('wicker')),
      surface: SURFACE.wood,
    },
    bread: {
      ...base,
      color: sampleSwatch(palette.roofTile, rng.fork('bread')),
      surface: SURFACE.wood,
    },
  };
  return new Map(DETAIL_KINDS.map((kind) => [kind, template(DETAIL_PARTS[kind], styles)]));
}
